import { Link } from 'react-router'
import { ScoreMark } from './score.tsx'

/**
 * The at-a-glance table above a ranking.
 *
 * Shared by /best/{category} and /{city}/{category}. It existed as two copies
 * that were supposed to be identical and were not: different rank-column
 * widths, different padding, and at one point one had a Card wrapper the other
 * had lost. Every visual defect on these pages this session started the same
 * way — the same markup pasted into two files and then tuned in one.
 *
 * ## Spacing is declared once for the row
 *
 * Two right-aligned numeric columns with no gap between them collide however
 * carefully each is styled on its own, which is exactly what happened when
 * Score and Rating were adjusted separately. Padding belongs to the column
 * rhythm here; only alignment varies per cell.
 */

export interface RankingTableRow {
	id: string
	slug: string
	name: string
	score: number | null
	ratingValue: number | null
	ratingCount: number | null
	bestFor: string | null
}

export function RankingTable({
	businesses,
}: {
	businesses: RankingTableRow[]
}) {
	if (businesses.length === 0) return null

	return (
		<div className="mb-10 overflow-x-auto">
			<table className="w-full border-collapse text-sm">
				<thead>
					<tr className="text-muted-foreground border-b">
						<th className="w-12 py-2 pr-4 text-right font-medium">#</th>
						<th className="py-2 pr-6 text-left font-medium">Business</th>
						<th className="w-20 py-2 pr-6 text-right font-medium">Score</th>
						<th className="w-32 py-2 pr-6 text-right font-medium">Rating</th>
						<th className="hidden py-2 text-left font-medium sm:table-cell">
							Best for
						</th>
					</tr>
				</thead>
				<tbody>
					{businesses.map((business, index) => (
						<tr
							key={business.id}
							className="hover:bg-muted/40 border-b transition-colors last:border-0"
						>
							<td className="text-muted-foreground w-12 py-2.5 pr-4 text-right font-mono tabular-nums">
								{index + 1}
							</td>
							<td className="py-2.5 pr-6">
								<Link
									className="font-medium hover:underline"
									to={`/business/${business.slug}`}
								>
									{business.name}
								</Link>
							</td>
							<td className="w-20 py-2.5 pr-6 text-right">
								{business.score != null ? (
									<ScoreMark score={business.score} size="plain" />
								) : null}
							</td>
							<td className="text-muted-foreground w-32 py-2.5 pr-6 text-right font-mono whitespace-nowrap tabular-nums">
								{business.ratingValue?.toFixed(1)}★{' '}
								<span className="text-muted-foreground/70">
									{(business.ratingCount ?? 0).toLocaleString()}
								</span>
							</td>
							<td className="text-muted-foreground hidden max-w-[18rem] truncate py-2.5 sm:table-cell">
								{business.bestFor ?? '—'}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	)
}
